'use client';

import Image from '@/components/Image';
import MagicJump from '@/components/MagicJump';
import { useInViewportAnimation } from '@/hooks/useInViewportAnimation'; // 导入动画hook
import { LeftArrowOutlined, RightArrowOutlined } from '@zeelool/icon';
import { useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import styles from './common.module.scss';

interface Part4ImageItem {
  src: string;
}

interface Part4Item {
  top_img: string;
  left_img?: string;
  title: string;
  desc: string;
  sub_title: string;
  font_color?: string;
  img_list: Part4ImageItem[];
  btn_name?: string;
  jump_target?: any;
}

interface Part4Data {
  title: string;
  content: string;
  bg_color?: string;
  list: Part4Item[];
}

interface Part4Props {
  data: Part4Data | null;
  isMobile: boolean;
}

export default function Part4({ data, isMobile }: Part4Props) {
  const [activeIndex, setActiveIndex] = useState(2); // PC端默认第3个为活跃状态
  const contentRef = useRef<HTMLDivElement>(null);

  const slideWidth = 346; // px to vw conversion needed
  const slideSpan = 24;

  const handleSlideClick = (index: number) => {
    if (index + 1 === activeIndex && index >= 0) {
      handleChange('left');
    } else if (index - 1 === activeIndex && index <= data.list.length - 1) {
      handleChange('right');
    }
  };

  const handleChange = (direction: 'left' | 'right') => {
    if (direction === 'right') {
      if (activeIndex >= data.list.length - 1) return;
      setActiveIndex((prev) => prev + 1);
    } else {
      if (activeIndex <= 0) return;
      setActiveIndex((prev) => prev - 1);
    }
  };

  // PC端3D轮播的transform计算
  useEffect(() => {
    if (!isMobile && contentRef.current) {
      const translateX = -(slideWidth * (activeIndex - 1.5) + slideSpan * (activeIndex - 2.5));
      contentRef.current.style.transform = `translateX(${translateX}px)`;
    }
  }, [activeIndex, isMobile]);

  // 键盘事件处理
  useEffect(() => {
    if (!isMobile) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'ArrowLeft') {
          handleChange('left');
        } else if (e.key === 'ArrowRight') {
          handleChange('right');
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isMobile]);

  const animationRef = useInViewportAnimation(
    () => {
      if (isMobile) return;
      try {
        const PartArr = [
          '.fit-guide-container-section-part4-title',
          '.fit-guide-container-section-part4-content',
        ];
        PartArr.forEach((item) => {
          const element = document.querySelector(item);
          if (element) {
            element.classList.add('fadeInDownAnimate');
          }
        });
      } catch {
        // 出错时直接显示
        const elements = document.querySelectorAll('.opacity0');
        elements.forEach((el) => {
          const element = el as HTMLElement; // 创建一个新的变量
          element.style.opacity = '1'; // 修改新变量的属性
        });
      }
    },
    { threshold: 0.3, once: true },
  );

  if (!data || !data.list?.length) {
    return null;
  }

  const containerStyle = data.bg_color ? { background: data.bg_color } : {};

  const navOptions = {
    spaceBetween: 10,
    slidesOffsetAfter: 14,
  };

  return (
    <div
      ref={(node) => {
        if (!isMobile && contentRef && typeof contentRef !== 'function') {
          (animationRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
      }}
      className="bg-[#c8bdff] pt-[40px] pb-[28px] px-0 overflow-hidden md:pt-[2.92vw] md:pb-0"
      style={containerStyle}
    >
      <h2
        className={`fit-guide-container-section-part4-title px-[28px] md:px-0 ${styles.section_title} ${isMobile ? styles['max-line-2'] : styles['max-line-1']} ${!isMobile ? 'opacity0' : ''}`}
        dangerouslySetInnerHTML={{ __html: data.title }}
      />
      <div
        className={`fit-guide-container-section-part4-content px-[28px] md:px-0 text-[13px] md:text-[16px] text-center mt-[8px] md:mt-[14px] ${isMobile ? styles['max-line-3'] : styles['max-line-1']} ${!isMobile ? 'opacity0' : ''}`}
        style={{ fontFamily: 'BricolageGrotesque' }}
        dangerouslySetInnerHTML={{ __html: data.content }}
      />

      {isMobile ? (
        // 移动端轮播
        <div className="nav-container mt-[24px]">
          <Swiper {...navOptions} slidesPerView="auto" className="swiper-mobile !pl-[14px]">
            {data.list.map((item, idx) => (
              <SwiperSlide key={idx} className="swiper-slide-mobile !w-[300px] ml-[10px]">
                <MagicJump
                  prefetch={false}
                  item={item.jump_target}
                  className="relative bg-white rounded-[12px] overflow-hidden min-h-[515px] block no-underline"
                >
                  <Image
                    src={item.top_img}
                    alt={item.title}
                    width={300}
                    height={206}
                    className="w-full aspect-[275/206] !rounded-none"
                  />
                  <div className="px-[12px] pb-[16px]" style={{ color: item.font_color }}>
                    <div
                      className="mt-[16px] text-[16px] leading-[20px]"
                      style={{ fontFamily: 'var(--font-bricolage-bold)' }}
                      dangerouslySetInnerHTML={{ __html: item.title }}
                    />
                    <div
                      className="mt-[12px] text-[12px] leading-[18px] font-[BricolageGrotesque]"
                      dangerouslySetInnerHTML={{ __html: item.desc }}
                    />
                    <div
                      className="mt-[20px] text-[16px] leading-[20px]"
                      style={{ fontFamily: 'var(--font-bricolage-bold)' }}
                    >
                      {item.sub_title}
                    </div>
                    <div className="grid grid-cols-4 gap-x-[16px] gap-y-[10px] mt-[12px] min-h-[82px]">
                      {item.img_list.map((img, imgIndex) => (
                        <Image
                          key={imgIndex}
                          src={img.src}
                          alt=""
                          width={50}
                          height={50}
                          className="w-full h-auto"
                        />
                      ))}
                    </div>
                    <div
                      className="flex justify-center items-center w-[230px] h-[43px] absolute bottom-[16px] left-1/2 -translate-x-1/2 bg-[#282828] rounded-[21.5px] text-[14px] text-white cursor-pointer"
                      style={{
                        fontFamily: 'var(--font-bricolage-bold)',
                      }}
                    >
                      {item.btn_name || 'Find My Fit'}
                    </div>
                  </div>
                </MagicJump>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      ) : (
        // PC端3D轮播
        <div
          className="mt-[2.08vw] mx-auto relative max-w-[1920px] w-screen h-[21.67vw] flex items-start transition-all duration-1000"
          ref={contentRef}
        >
          {data.list.map((item, index) => (
            <div
              key={index}
              className={`flex-shrink-0 relative ${
                index === activeIndex ? 'custom-slide-active' : ''
              } ${index + 1 === activeIndex ? 'prev' : ''} ${
                index - 1 === activeIndex ? 'next' : ''
              }`}
            >
              <MagicJump
                prefetch={false}
                item={item.jump_target}
                isAllowClick={activeIndex === index}
              >
                <div
                  className={`
            ${
              index === activeIndex
                ? 'w-[39.17vw] bg-white cursor-pointer'
                : 'w-[18.02vw] bg-transparent'
            }
            ${index + 1 === activeIndex || index - 1 === activeIndex ? 'cursor-pointer' : ''}
            rounded-[1.04vw] transition-all duration-1000 mr-[1.25vw] overflow-hidden flex items-start
          `}
                  onClick={() => handleSlideClick(index)}
                >
                  <div className="relative w-[18.02vw] h-[21.67vw] flex-shrink-0">
                    <Image
                      className={`absolute top-0 bottom-0 left-0 right-0 w-full h-full object-cover transition-all duration-1000 z-[1] ${
                        index === activeIndex ? 'opacity-0' : 'opacity-100'
                      }`}
                      src={item.top_img}
                      alt={item.title}
                      width={346}
                      height={416}
                    />
                    <Image
                      className={`absolute top-0 bottom-0 left-0 right-0 w-full h-full object-cover transition-all duration-1000 z-[1] ${
                        index === activeIndex ? 'opacity-100' : 'opacity-0'
                      }`}
                      src={item.left_img || item.top_img}
                      alt={item.title}
                      width={346}
                      height={416}
                    />
                  </div>

                  {/* 活跃状态时显示的右侧内容 */}
                  {index === activeIndex && (
                    <div className="flex-shrink-0 w-[21.15vw] box-border pt-[2.6vw] px-[1.04vw]">
                      <div
                        className="text-[1.25vw] leading-[1.46vw]"
                        style={{
                          color: item.font_color,
                          fontFamily: 'var(--font-bricolage-bold)',
                        }}
                      >
                        {item.title}
                      </div>
                      <p
                        className="text-[0.83vw] leading-[1.25vw] mt-[0.63vw] h-[2.5vw]"
                        style={{
                          color: item.font_color,
                          fontFamily: 'var(--font-instrument)',
                        }}
                      >
                        {item.desc}
                      </p>
                      <div
                        className="mt-[2.08vw] mb-[2.08vw] text-[min(1.25vw,24px)] leading-[min(1.46vw,28px)]"
                        style={{
                          color: item.font_color,
                          fontFamily: 'var(--font-bricolage-bold)',
                        }}
                      >
                        {item.sub_title}
                      </div>
                      <div className="flex flex-wrap items-start mt-[1.04vw] h-[4.58vw] overflow-y-auto">
                        {item.img_list.map((img, imgIndex) => (
                          <Image
                            key={imgIndex}
                            src={img.src}
                            alt="glasses"
                            width={80}
                            height={80}
                            className="w-[3.07vw] mr-[0.63vw] [&:nth-child(1)]:mb-[1.04vw] [&:nth-child(2)]:mb-[1.04vw] [&:nth-child(3)]:mb-[1.04vw] [&:nth-child(4)]:mb-[1.04vw] [&:nth-child(5)]:mb-[1.04vw]"
                          />
                        ))}
                      </div>
                      <div
                        className="flex justify-center items-center mt-[1.04vw] w-[14.58vw] h-[2.08vw] text-[0.73vw] bg-[#282828] rounded-[2.24vw] text-white cursor-pointer"
                        style={{ fontFamily: 'var(--font-bricolage-bold)' }}
                      >
                        {item.btn_name || 'Find My Fit'}
                      </div>
                    </div>
                  )}
                </div>
              </MagicJump>

              {/* 标题 - 只有在非活跃状态时显示 */}
              <div
                className={`
          absolute left-0 -bottom-[1.77vw] w-[18.02vw] text-center transition-all duration-1000 text-[min(1.25vw,24px)] leading-[min(1.46vw,28px)]
          ${index === activeIndex ? 'opacity-0' : 'opacity-100'}
        `}
                style={{
                  color: item.font_color,
                  fontFamily: 'var(--font-bricolage-bold)',
                }}
              >
                {item.title}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* PC端导航按钮 */}
      {!isMobile && (
        <div className="flex justify-center py-[2.5vw] pb-[1.67vw]">
          <div
            className={`flex justify-center items-center w-[2.08vw] h-[2.08vw] rounded-full border border-[#282828] cursor-pointer transition-all duration-300 mr-[1.67vw] ${
              activeIndex <= 0 ? 'opacity-30 cursor-not-allowed hover:bg-black/10' : ''
            }`}
            onClick={() => handleChange('left')}
          >
            <LeftArrowOutlined className="text-[min(0.833vw,16px)]" />
          </div>
          <div
            className={`flex justify-center items-center w-[2.08vw] h-[2.08vw] rounded-full border border-[#282828] cursor-pointer transition-all duration-300 ${
              activeIndex >= data.list.length - 1
                ? 'opacity-30 cursor-not-allowed hover:bg-black/10'
                : ''
            }`}
            onClick={() => handleChange('right')}
          >
            <RightArrowOutlined className="text-[min(0.833vw,16px)]" />
          </div>
        </div>
      )}
    </div>
  );
}
